import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useNavigate } from 'react-router-dom';
import { markNotificationAsRead, FONT_FAMILY } from "../common/sharedFunctions";
import moment from 'moment/min/moment-with-locales';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CampaignIcon from '@mui/icons-material/Campaign';

const BRAND = {
  teal: '#4fd1c5',
  tealDark: '#2dd4bf',
  tealTint: 'rgba(79,209,197,0.12)',
  slate950: '#020617',
  slate900: '#0f172a',
  slate700: '#334155',
  slate500: '#64748b',
  slate400: '#94a3b8',
  slate200: '#e2e8f0',
  slate100: '#f1f5f9',
  slate50: '#f8fafc',
  font: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif',
};

export default function FacilityNotifications() {
  const navigate = useNavigate();
  const notificationdata = useSelector(state => state.notificationdata);
  const bookinglistdata = useSelector(state => state.bookinglistdata);
  const usersdata = useSelector(state => state.usersdata);
  const auth = useSelector(state => state.auth);

  // Mark all unread as read on mount
  useEffect(() => {
    if (notificationdata.notifications) {
      notificationdata.notifications.forEach(n => {
        if (n?.isRead === false) {
          markNotificationAsRead({ notificationId: n?.id, userId: auth?.profile?.uid });
        }
      });
    }
  }, [notificationdata.notifications, auth?.profile?.uid]);

  const driverNameById = useMemo(() => {
    const map = {};
    (usersdata?.staticusers || []).forEach(u => {
      if (u && u.id) map[u.id] = `${u.firstName || ''} ${u.lastName || ''}`.trim();
    });
    return map;
  }, [usersdata?.staticusers]);

  const bookingByRef = useMemo(() => {
    const map = {};
    (bookinglistdata?.bookings || []).forEach(b => {
      if (b.reference) map[String(b.reference).toUpperCase()] = b;
      if (b.id) map[String(b.id).slice(-6).toUpperCase()] = b;
    });
    return map;
  }, [bookinglistdata?.bookings]);

  const enriched = useMemo(() => {
    const refRe = /\b([A-Z0-9]{6})\b/;
    const myUid = auth?.profile?.uid;
    const myRole = auth?.profile?.usertype;
    const bookingsArr = bookinglistdata?.bookings || [];

    const statusKeywords = [
      { re: /(hai raggiunto la destinazione|destination reached|completare il pagamento)/i, target: ['REACHED', 'PENDING', 'PAYMENT_PENDING'] },
      { re: /(ha completato|completed.*trip|trip completed)/i, target: ['COMPLETE', 'PAID'] },
      { re: /(conducente|trasportatore).*(comincia|iniziat|avviat|started)/i, target: ['STARTED'] },
      { re: /(conducente|trasportatore).*(arrivat|arrived)/i, target: ['ARRIVED'] },
      { re: /(accettat|accepted)/i, target: ['ACCEPTED'] },
      { re: /(offerta|bid|offer)/i, target: ['NEW'] },
    ];

    const findBooking = (msg, title, dated) => {
      const haystack = `${msg} ${title}`;
      const refMatch = haystack.match(refRe);
      if (refMatch && bookingByRef[refMatch[1]]) return bookingByRef[refMatch[1]];
      if (!dated || bookingsArr.length === 0) return null;
      const ts = typeof dated === 'number' ? dated : new Date(dated).getTime();
      if (isNaN(ts)) return null;
      const mine = bookingsArr.filter(b =>
        (myRole === 'customer' && b.customer === myUid) ||
        (myRole === 'fleetadmin' && b.fleetadmin === myUid) ||
        myRole === 'admin'
      );
      const kw = statusKeywords.find(k => k.re.test(haystack));
      if (!kw) return null;
      const candidates = mine
        .map(b => {
          const bts = b.bookingDate ? new Date(b.bookingDate).getTime() : 0;
          const dist = bts ? Math.abs(bts - ts) : Infinity;
          return { b, dist, statusMatch: kw.target.includes(b.status) };
        })
        .filter(c => c.dist < 7 * 86400000)
        .sort((a, b) => {
          if (a.statusMatch !== b.statusMatch) return a.statusMatch ? -1 : 1;
          return a.dist - b.dist;
        });
      return candidates.length ? candidates[0].b : null;
    };

    return (notificationdata.notifications || [])
      .map(n => {
        const msg = (n.msg || n.body || n.text || '').trim();
        const titleField = (n.title || '').trim();
        const isGenericTitle = !titleField || /^(hai una notifica|you have a notification)$/i.test(titleField);

        const linkedBooking = findBooking(msg, titleField, n.dated || n.dateString || n.createdAt);
        const ref = linkedBooking ? (linkedBooking.reference || (linkedBooking.id ? linkedBooking.id.slice(-6).toUpperCase() : null)) : null;
        const dName = linkedBooking
          ? (linkedBooking.driver_name || (linkedBooking.driver && driverNameById[linkedBooking.driver]) || '')
          : '';

        const cleanMsg = msg
          .replace(/\bil tuo id di prenotazione[\s\S]*$/i, '')
          .replace(/\bbooking id[\s\S]*$/i, '')
          .replace(/\bil\s+(conducente|trasportatore|driver)\b/gi, dName ? `il trasportatore ${dName}` : 'il trasportatore')
          .replace(/[.\s]+$/, '')
          .trim();

        const finalTitle = (isGenericTitle && cleanMsg) ? cleanMsg : (titleField || cleanMsg || 'Notifica di sistema');
        const finalMsg = (cleanMsg && cleanMsg !== finalTitle) ? cleanMsg : '';

        return {
          ...n,
          _title: finalTitle,
          _msg: finalMsg,
          _ref: ref,
          _booking: linkedBooking,
          _driverName: dName,
        };
      })
      .sort((a, b) => (b.dated || 0) - (a.dated || 0));
  }, [notificationdata.notifications, bookingByRef, driverNameById, bookinglistdata?.bookings, auth?.profile?.uid, auth?.profile?.usertype]);

  if (notificationdata.loading) return <CircularLoading />;

  const renderCard = (n) => {
    const dateLabel = n.dated ? moment(n.dated).format('LLLL') : '';
    const relative = n.dated ? moment(n.dated).fromNow() : '';
    const navigable = !!n._booking;

    return (
      <Box
        key={n.id}
        onClick={() => navigable && navigate(`/bookings/bookingdetails/${n._booking.id}`)}
        sx={{
          mb: 2,
          backgroundColor: '#fff',
          borderRadius: '14px',
          border: `1px solid ${BRAND.slate200}`,
          padding: 2.25,
          transition: 'all 0.15s ease',
          cursor: navigable ? 'pointer' : 'default',
          '&:hover': navigable ? {
            borderColor: BRAND.teal,
            boxShadow: '0 4px 16px rgba(79,209,197,0.12)',
          } : {},
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '12px',
            backgroundColor: BRAND.tealTint, color: BRAND.tealDark,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <CampaignIcon sx={{ fontSize: 22 }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Typography sx={{
                fontFamily: BRAND.font, fontSize: 14, fontWeight: 700,
                color: BRAND.slate900, lineHeight: 1.35,
              }}>
                {n._title}
              </Typography>
              {navigable && (
                <IconButton size="small" sx={{ color: BRAND.slate400, mt: -0.25, mr: -0.5 }}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {n._msg && (
              <Typography sx={{ fontFamily: BRAND.font, fontSize: 13, color: BRAND.slate700, mb: 1 }}>
                {n._msg}
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mt: 1 }}>
              {n._ref && (
                <Chip
                  label={`#${n._ref}`}
                  size="small"
                  sx={{
                    fontFamily: BRAND.font,
                    fontSize: 11, fontWeight: 700,
                    height: 22, color: BRAND.tealDark,
                    backgroundColor: BRAND.tealTint,
                    border: 'none',
                  }}
                />
              )}
              {n._driverName && (
                <Chip
                  label={`Trasportatore: ${n._driverName}`}
                  size="small"
                  sx={{
                    fontFamily: BRAND.font,
                    fontSize: 11, fontWeight: 500,
                    height: 22, color: BRAND.slate700,
                    backgroundColor: BRAND.slate100,
                    border: 'none',
                  }}
                />
              )}
              {n._booking?.status && (
                <Chip
                  label={n._booking.status}
                  size="small"
                  sx={{
                    fontFamily: BRAND.font,
                    fontSize: 11, fontWeight: 600,
                    height: 22, color: BRAND.slate700,
                    backgroundColor: BRAND.slate100,
                    border: 'none',
                  }}
                />
              )}
              {n._booking?.pickupAddress && (
                <Typography sx={{ fontFamily: BRAND.font, fontSize: 11, color: BRAND.slate500, ml: 0.5, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`Da: ${n._booking.pickupAddress}`}>
                  📍 {n._booking.pickupAddress}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
              <Typography sx={{ fontFamily: BRAND.font, fontSize: 11, color: BRAND.slate500, fontWeight: 600 }}>
                {relative}
              </Typography>
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: BRAND.slate200 }} />
              <Typography sx={{ fontFamily: BRAND.font, fontSize: 11, color: BRAND.slate400 }}>
                {dateLabel}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, fontFamily: BRAND.font }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: BRAND.font, fontWeight: 700, fontSize: 22, color: BRAND.slate900, letterSpacing: '-0.015em' }}>
            Notifiche
          </Typography>
          <Typography sx={{ fontFamily: BRAND.font, fontSize: 13, color: BRAND.slate500 }}>
            Aggiornamenti su prenotazioni, offerte e messaggi di sistema
          </Typography>
        </Box>
        <Chip
          label={`${enriched.length} ${enriched.length === 1 ? 'notifica' : 'notifiche'}`}
          sx={{
            fontFamily: BRAND.font, fontWeight: 600, fontSize: 12,
            backgroundColor: BRAND.tealTint, color: BRAND.tealDark, border: 'none',
          }}
        />
      </Box>

      {enriched.length === 0 ? (
        <Box sx={{ p: 6, textAlign: 'center', backgroundColor: '#fff', borderRadius: '14px', border: `1px dashed ${BRAND.slate200}` }}>
          <CampaignIcon sx={{ fontSize: 48, color: BRAND.slate200, mb: 1 }} />
          <Typography sx={{ fontFamily: BRAND.font, fontSize: 15, fontWeight: 600, color: BRAND.slate900 }}>
            Nessuna notifica
          </Typography>
          <Typography sx={{ fontFamily: BRAND.font, fontSize: 13, color: BRAND.slate500, mt: 0.5 }}>
            Quando riceverai aggiornamenti compariranno qui.
          </Typography>
        </Box>
      ) : (
        <Box>
          {enriched.map(renderCard)}
        </Box>
      )}
    </Box>
  );
}
